import { ARENA_KINDS, ARENA_SPORT, type Aabb, type ArenaKind, type ArenaSport, type InteriorTemplate, type Vec2 } from "@townsy/contracts";
import { YAW_EAST, YAW_NORTH, YAW_SOUTH, YAW_WEST, aabb, aabbContains } from "../../geometry";
import { fixture } from "../common";

/**
 * Sports Park stadiums (ADR-0044): the interiors of the five venues, one shared room each, where fans watch the live AI
 * game from the stands. Everything the server's simulation and the browser's renderer both need (the playing surface
 * and its markings, the stands and their seats, the scoreboard) is exported from here so they cannot disagree.
 *
 * Local coordinates: x grows east, z grows north; the entrance and exit door are on the south wall at x = 0. The four
 * rectangular sports put the field's long axis along x, centred at the origin, inside a bowl:
 *
 *   the south wall (door) | the entry concourse | the SOUTH stand (home fans), split by a centre aisle |
 *   the walkway ring around the field | the field (fenced off: fans never walk onto it) | the walkway |
 *   the NORTH stand (away fans) | the north wall;   WEST and EAST end stands (crowd only) at the ends.
 *
 * Baseball has home plate near the south end and centre field to the north: the home fans sit along the first-base line
 * (the EAST stand, facing west), the away fans along the third-base line (the WEST stand, facing east), with crowd-only
 * stands behind home plate and bleachers beyond centre field.
 *
 * Every fan seat is a seat of the venue's own seat list (the race track pattern, ADR-0016): the game seats a fan in the
 * free seat of their chosen section nearest to them, and the browser draws them sitting there (`VenueSceneHandle.sitAt`).
 * The stands are colliders (`venue_` fixtures, drawn by the venue's own 3D): fans walk on the concourse and the walkway.
 */

// ---------------------------------------------------------------------------------------------
// Playing surfaces (real proportions)
// ---------------------------------------------------------------------------------------------

interface FieldBase {
  /** Centre of the playing surface. */
  cx: number;
  cz: number;
  /** Size along x (length) and z (width) of the playing surface (baseball: its bounding box). */
  length: number;
  width: number;
}

/** American football: 120 x 53 1/3 yards including the two 10-yard end zones; goalposts on the end lines. */
export interface FootballField extends FieldBase {
  sport: "football";
  yard: number;
  endZone: number;
  /** x of the goal lines (the end zones are beyond them) and of the end lines. */
  goalLineX: number;
  endLineX: number;
  goalpost: { crossbarY: number; width: number; uprightTop: number };
}

/** Soccer: 105 x 68 m pitch. */
export interface SoccerField extends FieldBase {
  sport: "soccer";
  goal: { width: number; height: number };
  penaltyArea: { depth: number; width: number };
  goalArea: { depth: number; width: number };
  centreCircle: number;
  penaltySpot: number;
}

/** Baseball: the diamond's bases 90 ft apart, the mound 60 ft 6 in from home, the outfield wall from pole to centre. */
export interface BaseballField extends FieldBase {
  sport: "baseball";
  home: Vec2;
  /** Distance between bases (27.43 m). */
  base: number;
  /** First, second and third base, and the pitcher's rubber. */
  first: Vec2;
  second: Vec2;
  third: Vec2;
  mound: Vec2;
  /** Distance from home plate to the outfield wall at the foul poles and in centre field. */
  wall: { pole: number; centre: number; height: number };
  /** The infield dirt arc's radius around the mound. */
  infieldRadius: number;
  /** The backstop behind home plate. */
  backstopZ: number;
}

/** Basketball: 94 x 50 ft court; rims 10 ft high, 63 in from the baseline. */
export interface BasketballField extends FieldBase {
  sport: "basketball";
  /** |x| of the rim centres. */
  hoopX: number;
  rimY: number;
  rimRadius: number;
  /** |x| of the backboards. */
  backboardX: number;
  threePoint: { radius: number; corner: number; cornerLength: number };
  key: { width: number; length: number };
  centreCircle: number;
}

/** Ice hockey: 200 x 85 ft rink with 28 ft corners; goal lines 11 ft from the end boards. */
export interface HockeyField extends FieldBase {
  sport: "hockey";
  cornerRadius: number;
  goalLineX: number;
  goal: { width: number; height: number };
  blueLineX: number;
  centreCircle: number;
  /** End-zone faceoff spots: |x| and |z|. */
  faceoff: { x: number; z: number; radius: number };
}

export type ArenaField = FootballField | SoccerField | BaseballField | BasketballField | HockeyField;

const YD = 0.9144;
const FT = 0.3048;

const FOOTBALL: FootballField = {
  sport: "football",
  cx: 0,
  cz: 0,
  length: 120 * YD,
  width: (160 / 3) * YD,
  yard: YD,
  endZone: 10 * YD,
  goalLineX: 50 * YD,
  endLineX: 60 * YD,
  goalpost: { crossbarY: 10 * FT, width: 18.5 * FT, uprightTop: 10 * FT + 35 * FT },
};

const SOCCER: SoccerField = {
  sport: "soccer",
  cx: 0,
  cz: 0,
  length: 105,
  width: 68,
  goal: { width: 7.32, height: 2.44 },
  penaltyArea: { depth: 16.5, width: 40.32 },
  goalArea: { depth: 5.5, width: 18.32 },
  centreCircle: 9.15,
  penaltySpot: 11,
};

const BASKETBALL: BasketballField = {
  sport: "basketball",
  cx: 0,
  cz: 0,
  length: 94 * FT,
  width: 50 * FT,
  hoopX: 47 * FT - 63 * 0.0254,
  rimY: 10 * FT,
  rimRadius: 0.2286,
  backboardX: 47 * FT - 4 * FT,
  threePoint: { radius: 23.75 * FT, corner: 22 * FT, cornerLength: 14 * FT },
  key: { width: 16 * FT, length: 19 * FT },
  centreCircle: 6 * FT,
};

const HOCKEY: HockeyField = {
  sport: "hockey",
  cx: 0,
  cz: 0,
  length: 200 * FT,
  width: 85 * FT,
  cornerRadius: 28 * FT,
  goalLineX: 89 * FT,
  goal: { width: 6 * FT, height: 4 * FT },
  blueLineX: 25 * FT,
  centreCircle: 15 * FT,
  faceoff: { x: 69 * FT, z: 22 * FT, radius: 15 * FT },
};

const BB_HOME: Vec2 = { x: 0, z: -40 };
const BB_BASE = 90 * FT;
const BB_D = BB_BASE / Math.SQRT2;
const BASEBALL: BaseballField = {
  sport: "baseball",
  // The bounding box of fair territory up to the wall (the poles at +-45 degrees, centre field due north).
  cx: 0,
  cz: BB_HOME.z + 55,
  length: 2 * 92 * Math.SQRT1_2,
  width: 110,
  home: BB_HOME,
  base: BB_BASE,
  first: { x: BB_HOME.x + BB_D, z: BB_HOME.z + BB_D },
  second: { x: BB_HOME.x, z: BB_HOME.z + 2 * BB_D },
  third: { x: BB_HOME.x - BB_D, z: BB_HOME.z + BB_D },
  mound: { x: BB_HOME.x, z: BB_HOME.z + 60.5 * FT },
  wall: { pole: 92, centre: 110, height: 2.6 },
  infieldRadius: 95 * FT,
  backstopZ: BB_HOME.z - 10,
};

// ---------------------------------------------------------------------------------------------
// Stands and seats
// ---------------------------------------------------------------------------------------------

export type ArenaSection = "home" | "away";

/** A block of tiered rows. Rows rise away from the field; the front row is the one nearest the field. */
export interface ArenaStand {
  id: string;
  /** Fan section (seats you can take) or "crowd" (drawn with a crowd only). */
  section: ArenaSection | "crowd";
  /** Footprint (its collider). */
  box: Aabb;
  /** The way seated fans face (towards the field). */
  facing: number;
  rows: number;
  rowDepth: number;
  rowRise: number;
  /** Height of the front row's tread above the floor. */
  firstRowY: number;
  seatPitch: number;
}

export interface ArenaSeat {
  /** Index in the stadium's seat list (`arenaSeatsOf`). */
  index: number;
  section: ArenaSection;
  stand: string;
  /** 0 = front row. */
  row: number;
  /** Where a seated fan's hips are (on the row's tread). */
  x: number;
  /** Height of the row's tread (the seated avatar's feet). */
  y: number;
  z: number;
  facing: number;
}

export interface ArenaScoreboard {
  /** Centre of the screen (x, z) and the height of its bottom edge. */
  x: number;
  z: number;
  bottomY: number;
  width: number;
  height: number;
  /** "end": one screen facing the field (outdoor); "center": a four-sided screen hung over the court (indoor). */
  style: "end" | "center";
  facing: number;
}

export interface ArenaLayout {
  kind: ArenaKind;
  sport: ArenaSport;
  room: Aabb;
  field: ArenaField;
  /** Where fans may not walk (the field and its sidelines, or the baseball outfield): colliders. */
  fieldBlocks: readonly Aabb[];
  /** The fenced field-level area drawn with boards / a rail (rectangular sports: one box; baseball: the backstop strip). */
  fence: Aabb;
  stands: readonly ArenaStand[];
  scoreboard: ArenaScoreboard;
  /** Indoor arena (a roof ring and hanging lights) or outdoor stadium (light towers). */
  indoor: boolean;
}

interface BowlSpec {
  kind: ArenaKind;
  field: ArenaField;
  /** Sideline space inside the fence beyond the playing surface: at the ends (x) and the sides (z). */
  marginX: number;
  marginZ: number;
  /** Width of the walkway ring outside the fence. */
  walkway: number;
  sideRows: number;
  endRows: number;
  rowDepth: number;
  rowRise: number;
  firstRowY: number;
  seatPitch: number;
  aisleHalf: number;
  /** Depth of the entry concourse between the south stand and the south wall. */
  concourse: number;
  indoor: boolean;
}

const r2 = (v: number) => Math.round(v * 100) / 100;

/** A rectangular sport's bowl: fenced field, walkway ring, south / north side stands (split by an aisle), end stands. */
function bowl(s: BowlSpec): ArenaLayout {
  const f = s.field;
  const fence = aabb(r2(f.cx - f.length / 2 - s.marginX), r2(f.cz - f.width / 2 - s.marginZ), r2(f.cx + f.length / 2 + s.marginX), r2(f.cz + f.width / 2 + s.marginZ));
  const sideDepth = s.sideRows * s.rowDepth;
  const endDepth = s.endRows * s.rowDepth;
  const southFront = fence.minZ - s.walkway;
  const southBack = southFront - sideDepth;
  const northFront = fence.maxZ + s.walkway;
  const northBack = northFront + sideDepth;
  const westFront = fence.minX - s.walkway;
  const westBack = westFront - endDepth;
  const eastFront = fence.maxX + s.walkway;
  const eastBack = eastFront + endDepth;
  const room = aabb(r2(westBack - 0.6), r2(southBack - s.concourse), r2(eastBack + 0.6), r2(northBack + 0.6));
  const rows = { rowDepth: s.rowDepth, rowRise: s.rowRise, firstRowY: s.firstRowY, seatPitch: s.seatPitch };
  const a = s.aisleHalf;
  const stands: ArenaStand[] = [
    { id: "south_w", section: "home", box: aabb(fence.minX, r2(southBack), -a, r2(southFront)), facing: YAW_NORTH, rows: s.sideRows, ...rows },
    { id: "south_e", section: "home", box: aabb(a, r2(southBack), fence.maxX, r2(southFront)), facing: YAW_NORTH, rows: s.sideRows, ...rows },
    { id: "north_w", section: "away", box: aabb(fence.minX, r2(northFront), -a, r2(northBack)), facing: YAW_SOUTH, rows: s.sideRows, ...rows },
    { id: "north_e", section: "away", box: aabb(a, r2(northFront), fence.maxX, r2(northBack)), facing: YAW_SOUTH, rows: s.sideRows, ...rows },
    { id: "west", section: "crowd", box: aabb(r2(westBack), fence.minZ, r2(westFront), fence.maxZ), facing: YAW_EAST, rows: s.endRows, ...rows },
    { id: "east", section: "crowd", box: aabb(r2(eastFront), fence.minZ, r2(eastBack), fence.maxZ), facing: YAW_WEST, rows: s.endRows, ...rows },
  ];
  const endTop = s.firstRowY + (s.endRows - 1) * s.rowRise + 1.2;
  const scoreboard: ArenaScoreboard = s.indoor
    ? { x: f.cx, z: f.cz, bottomY: 9.5, width: 7.2, height: 4, style: "center", facing: YAW_SOUTH }
    : { x: r2(eastBack - 2.5), z: f.cz, bottomY: r2(endTop + 1.5), width: 26, height: 11, style: "end", facing: YAW_WEST };
  return { kind: s.kind, sport: f.sport, room, field: f, fieldBlocks: [fence], fence, stands, scoreboard, indoor: s.indoor };
}

/** Baseball: the diamond near the south wall, side stands along the base lines, the outfield beyond (no walking). */
function ballpark(): ArenaLayout {
  const f = BASEBALL;
  const rows = { rowDepth: 0.85, rowRise: 0.4, firstRowY: 1.1, seatPitch: 0.72 };
  const sideRows = 9;
  const backRows = 8;
  const fenceX = 30;
  const walkway = 4;
  const sideFront = fenceX + walkway;
  const sideBack = sideFront + sideRows * rows.rowDepth;
  // The side stands end where the foul line is still well clear of them (fair when z - home.z >= |x|).
  const sideMinZ = f.backstopZ;
  const sideMaxZ = -12;
  const behindFront = f.backstopZ - walkway;
  const behindBack = behindFront - backRows * rows.rowDepth;
  const room = aabb(-70, r2(behindBack - 4.4), 70, 82);
  const centreWallZ = f.home.z + f.wall.centre;
  const stands: ArenaStand[] = [
    { id: "first_base", section: "home", box: aabb(sideFront, sideMinZ, r2(sideBack), sideMaxZ), facing: YAW_WEST, rows: sideRows, ...rows },
    { id: "third_base", section: "away", box: aabb(r2(-sideBack), sideMinZ, -sideFront, sideMaxZ), facing: YAW_EAST, rows: sideRows, ...rows },
    { id: "behind_w", section: "crowd", box: aabb(-fenceX, r2(behindBack), -3, behindFront), facing: YAW_NORTH, rows: backRows, ...rows },
    { id: "behind_e", section: "crowd", box: aabb(3, r2(behindBack), fenceX, behindFront), facing: YAW_NORTH, rows: backRows, ...rows },
    { id: "bleachers", section: "crowd", box: aabb(-34, r2(centreWallZ + 2.2), 34, r2(centreWallZ + 2.2 + 6 * rows.rowDepth)), facing: YAW_SOUTH, rows: 6, ...rows },
  ];
  // Fans may not walk onto the field: the strip from the backstop to centre field, and both outfield sides beyond the
  // end of the side stands.
  const fieldBlocks = [aabb(-fenceX, f.backstopZ, fenceX, room.maxZ), aabb(fenceX, sideMaxZ, room.maxX, room.maxZ), aabb(room.minX, sideMaxZ, -fenceX, room.maxZ)];
  const scoreboard: ArenaScoreboard = { x: 0, z: r2(centreWallZ + 2.2 + 6 * rows.rowDepth + 1.2), bottomY: 6.2, width: 26, height: 11, style: "end", facing: YAW_SOUTH };
  return {
    kind: "baseball_stadium",
    sport: "baseball",
    room,
    field: f,
    fieldBlocks,
    fence: aabb(-fenceX, f.backstopZ, fenceX, f.backstopZ + 0.3),
    stands,
    scoreboard,
    indoor: false,
  };
}

const OUTDOOR_ROWS = { rowDepth: 0.85, rowRise: 0.4, firstRowY: 1.1, seatPitch: 0.72, aisleHalf: 3, concourse: 4.4 };
const INDOOR_ROWS = { rowDepth: 0.8, rowRise: 0.38, firstRowY: 1.0, seatPitch: 0.68, aisleHalf: 2.5, concourse: 4.2 };

const LAYOUTS: Readonly<Record<ArenaKind, ArenaLayout>> = {
  football_stadium: bowl({ kind: "football_stadium", field: FOOTBALL, marginX: 5, marginZ: 6, walkway: 4, sideRows: 11, endRows: 9, indoor: false, ...OUTDOOR_ROWS }),
  soccer_stadium: bowl({ kind: "soccer_stadium", field: SOCCER, marginX: 4, marginZ: 4.5, walkway: 4, sideRows: 11, endRows: 9, indoor: false, ...OUTDOOR_ROWS }),
  baseball_stadium: ballpark(),
  basketball_arena: bowl({ kind: "basketball_arena", field: BASKETBALL, marginX: 3.2, marginZ: 3, walkway: 3, sideRows: 9, endRows: 8, indoor: true, ...INDOOR_ROWS }),
  hockey_arena: bowl({ kind: "hockey_arena", field: HOCKEY, marginX: 1.4, marginZ: 1.4, walkway: 3, sideRows: 9, endRows: 8, indoor: true, ...INDOOR_ROWS }),
};

/** Along-the-row and away-from-the-field axes of a stand. */
function standFrame(s: ArenaStand): { front: number; back: number; lateral: [number, number]; alongX: boolean; out: number } {
  const b = s.box;
  // Facing north: the front edge is the north edge, rows go south (out = -1 along z).
  if (Math.abs(s.facing - YAW_NORTH) < 1e-9) return { front: b.maxZ, back: b.minZ, lateral: [b.minX, b.maxX], alongX: true, out: -1 };
  if (Math.abs(s.facing - YAW_SOUTH) < 1e-9) return { front: b.minZ, back: b.maxZ, lateral: [b.minX, b.maxX], alongX: true, out: 1 };
  if (Math.abs(s.facing - YAW_EAST) < 1e-9) return { front: b.maxX, back: b.minX, lateral: [b.minZ, b.maxZ], alongX: false, out: -1 };
  return { front: b.minX, back: b.maxX, lateral: [b.minZ, b.maxZ], alongX: false, out: 1 };
}

/** Every seat spot of a stand, front row first (fan seats and the crowd's places alike). */
export function arenaStandSpots(s: ArenaStand): Array<{ row: number; x: number; y: number; z: number; facing: number }> {
  const fr = standFrame(s);
  const [lo, hi] = fr.lateral;
  const usable = hi - lo - 0.6;
  const count = Math.max(1, Math.floor(usable / s.seatPitch) + 1);
  const first = lo + (hi - lo - (count - 1) * s.seatPitch) / 2;
  const out: Array<{ row: number; x: number; y: number; z: number; facing: number }> = [];
  for (let row = 0; row < s.rows; row++) {
    // Hips over the back half of the tread; the feet rest near its front edge.
    const depth = fr.front + fr.out * (row * s.rowDepth + s.rowDepth * 0.68);
    for (let k = 0; k < count; k++) {
      const lateral = r2(first + k * s.seatPitch);
      const along = r2(depth);
      out.push({ row, x: fr.alongX ? lateral : along, z: fr.alongX ? along : lateral, y: r2(s.firstRowY + row * s.rowRise), facing: s.facing });
    }
  }
  return out;
}

function buildSeats(layout: ArenaLayout): ArenaSeat[] {
  const seats: ArenaSeat[] = [];
  for (const s of layout.stands) {
    if (s.section === "crowd") continue;
    for (const spot of arenaStandSpots(s)) seats.push({ index: seats.length, section: s.section, stand: s.id, ...spot });
  }
  return seats;
}

const SEATS: Readonly<Record<ArenaKind, readonly ArenaSeat[]>> = Object.fromEntries(ARENA_KINDS.map((k) => [k, buildSeats(LAYOUTS[k])])) as Record<ArenaKind, ArenaSeat[]>;

// ---------------------------------------------------------------------------------------------
// Templates
// ---------------------------------------------------------------------------------------------

/** Fixture colliders: the field (fans stay off it) and every stand. `venue_` = drawn by the venue's own 3D. */
export function arenaColliders(kind: ArenaKind) {
  const l = LAYOUTS[kind];
  return [...l.fieldBlocks.map((b, i) => fixture(i === 0 ? "venue_arena_field" : `venue_arena_field_${i}`, b)), ...l.stands.map((s) => fixture(`venue_arena_stand_${s.id}`, s.box))];
}

function template(kind: ArenaKind): InteriorTemplate {
  const l = LAYOUTS[kind];
  const sb = l.scoreboard;
  const fwd = { x: Math.sin(sb.facing), z: Math.cos(sb.facing) };
  return {
    id: kind,
    venue: kind,
    bounds: l.room,
    spawn: { position: { x: 0, z: r2(l.room.minZ + 2.4) }, facing: YAW_NORTH },
    exit: { position: { x: 0, z: r2(l.room.minZ + 1) }, facing: YAW_SOUTH },
    exitDoor: { position: { x: 0, z: l.room.minZ }, direction: { x: 0, z: -1 }, halfWidth: 1.6 },
    // The venue's name on top of the scoreboard, facing the same way.
    signAnchor: {
      kind: "storefront",
      position: { x: r2(sb.x + fwd.x * 0.15), y: r2(sb.bottomY + sb.height + (sb.style === "center" ? 1.6 : 2.4)), z: r2(sb.z + fwd.z * 0.15) },
      facing: sb.facing,
      width: sb.style === "center" ? 7 : 16,
      height: sb.style === "center" ? 1.6 : 3.2,
    },
    colliders: arenaColliders(kind),
  };
}

/** The five stadium interiors (one shared room each), keyed by civic kind; the template id and the venue are the kind. */
export const ARENA_INTERIORS: Readonly<Record<ArenaKind, InteriorTemplate>> = Object.fromEntries(ARENA_KINDS.map((k) => [k, template(k)])) as Record<ArenaKind, InteriorTemplate>;

// ---------------------------------------------------------------------------------------------
// Queries shared by the server and the browser
// ---------------------------------------------------------------------------------------------

export function arenaLayoutOf(kind: ArenaKind): ArenaLayout {
  return LAYOUTS[kind];
}

export function arenaFieldOf(kind: ArenaKind): ArenaField {
  return LAYOUTS[kind].field;
}

/** The fan seats (home and away sections) of a stadium; index = position in this list. */
export function arenaSeatsOf(kind: ArenaKind): readonly ArenaSeat[] {
  return SEATS[kind];
}

export function arenaScoreboardOf(kind: ArenaKind): ArenaScoreboard {
  return LAYOUTS[kind].scoreboard;
}

export function arenaStandsOf(kind: ArenaKind): readonly ArenaStand[] {
  return LAYOUTS[kind].stands;
}

export function arenaSportOf(kind: ArenaKind): ArenaSport {
  return ARENA_SPORT[kind];
}

/** A seated fan who moved this far from the seat walked away (the seat is freed). */
export const ARENA_SEAT_LEAVE_DISTANCE = 0.5;

/** The free seat of `section` nearest to (x, z), or null when the section is full. Ties go to the lower index. */
export function arenaNearestFreeSeat(kind: ArenaKind, section: ArenaSection, x: number, z: number, taken: ReadonlySet<number>): ArenaSeat | null {
  let best: ArenaSeat | null = null;
  let bestD = Number.POSITIVE_INFINITY;
  for (const seat of SEATS[kind]) {
    if (seat.section !== section || taken.has(seat.index)) continue;
    const d = Math.hypot(seat.x - x, seat.z - z);
    if (d < bestD) {
      best = seat;
      bestD = d;
    }
  }
  return best;
}

/** True when (x, z) is inside the room and outside every collider of the stadium (a spot a fan can stand on). */
export function arenaWalkable(kind: ArenaKind, x: number, z: number, radius = 0.3): boolean {
  const l = LAYOUTS[kind];
  if (x < l.room.minX + radius || x > l.room.maxX - radius || z < l.room.minZ + radius || z > l.room.maxZ - radius) return false;
  for (const c of arenaColliders(kind)) {
    const b = c.aabb;
    if (x > b.minX - radius && x < b.maxX + radius && z > b.minZ - radius && z < b.maxZ + radius) return false;
  }
  return true;
}

/** Baseball: inside fair territory (between the foul lines, up to the wall). */
export function baseballIsFair(field: BaseballField, x: number, z: number): boolean {
  const dz = z - field.home.z;
  const dx = x - field.home.x;
  if (dz < Math.abs(dx)) return false;
  return Math.hypot(dx, dz) <= baseballWallDistance(field, Math.atan2(dx, dz));
}

/** Baseball: distance from home plate to the outfield wall at bearing `yaw` (0 = centre field, +-PI/4 = the poles). */
export function baseballWallDistance(field: BaseballField, yaw: number): number {
  const t = Math.min(1, Math.abs(yaw) / (Math.PI / 4));
  // A smooth arc from the centre-field distance to the pole distance.
  return field.wall.centre + (field.wall.pole - field.wall.centre) * t * t;
}

/** Hockey: inside the rink's rounded rectangle. */
export function hockeyInsideRink(field: HockeyField, x: number, z: number, margin = 0): boolean {
  const hx = field.length / 2 - margin;
  const hz = field.width / 2 - margin;
  const r = Math.max(0, field.cornerRadius - margin);
  const dx = Math.abs(x - field.cx);
  const dz = Math.abs(z - field.cz);
  if (dx > hx || dz > hz) return false;
  if (dx <= hx - r || dz <= hz - r) return true;
  return Math.hypot(dx - (hx - r), dz - (hz - r)) <= r;
}

export function insideAabb(b: Aabb, x: number, z: number): boolean {
  return aabbContains(b, x, z);
}
